import { motion } from "framer-motion";
import { Landmark } from "lucide-react";
import { useT } from "@/i18n";

const item = {
  hidden: { opacity: 0, y: 32 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function About() {
  const { t } = useT();
  const CHAPTERS = t.about.chapters;

  return (
    <section id="biuro-wirtualne-o-nas" data-testid="about-section" className="py-16 sm:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-slate-600 dark:text-slate-400 font-semibold"
        >
          <span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-teal mr-3 align-middle" />{t.about.overline}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-4 max-w-2xl"
        >
          {t.about.heading}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          data-testid="about-since-2014"
          className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed"
        >
          {t.about.since_1} <strong className="text-slate-900 dark:text-white">{t.about.since_bold}</strong> {t.about.since_2}
        </motion.p>

        <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.n}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={item}
              data-testid={`about-chapter-${c.n}`}
              className={`group bg-white dark:bg-slate-950 p-6 sm:p-8 lg:p-10 transition-colors duration-300 hover:bg-slate-50 dark:hover:bg-slate-900 ${i === CHAPTERS.length - 1 && CHAPTERS.length % 2 ? "md:col-span-2" : ""}`}
            >
              <div className={`font-mono text-sm font-semibold ${["text-brand-teal", "text-brand-red", "text-brand-yellow", "text-brand-navy dark:text-slate-300", "text-brand-teal", "text-brand-red"][i % 6]}`}>{c.n}</div>
              <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight mt-4 transition-transform duration-300 group-hover:translate-x-1">
                {c.title}
              </h3>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed mt-3">{c.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          data-testid="about-offices"
          className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 sm:p-8 lg:p-10"
        >
          <div className="lg:col-span-5">
            <div className="w-10 h-10 rounded-xl bg-brand-yellow/15 text-brand-yellow flex items-center justify-center">
              <Landmark size={18} />
            </div>
            <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight mt-4">{t.about.offices_title}</h3>
            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed mt-3">{t.about.offices_intro}</p>
          </div>
          <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {t.contact.offices.map((o, i) => (
              <li key={o} data-testid={`about-office-${i}`} className="flex gap-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                <span className="font-mono text-xs text-brand-teal mt-0.5 shrink-0">0{i + 1}</span>
                {o}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}
